<template>
  <div class="relative flex flex-col h-auto justify-between md:rounded-t-lg min-w-0">
    <div class="flex w-full items-center align-middle gap-4 truncate justify-between">
      <div class="flex items-center gap-5 w-full">
        <slot name="icon">
          <div v-if="icon" class="flex items-center justify-center">
            <Icon :name="icon" class="h-8 w-8 text-dark" />
          </div>
        </slot>
        <div>
          <div class="">
            <slot name="overtitle">
              <div
                v-if="overtitle"
                class="text-sm md:text-base text-stronger font-semibold opacity-40 font-inter"
              >
                {{ overtitle }}
              </div>
            </slot>
          </div>
          <slot name="title">
            <div class="text-2xl md:text-3xl text-strongest font-semibold">
              {{ title }}
            </div>
          </slot>
        </div>
      </div>
      <div>
        <slot name="ctas"></slot>
      </div>
    </div>
    <!-- Skipped entirely when there is nothing to show, so pages without nuggets
    (e.g. Portfolio) don't inherit this row's top margin as dead space. -->
    <div
      v-if="headerNuggets.length || $slots.nuggets"
      class="mt-5 flex flex-row flex-wrap items-center md:mt-9 gap-y-5 gap-x-0 md:gap-x-4 md:gap-y-4 min-w-0"
      :class="headerNuggets.length >= 4 ? 'w-full' : `w-auto`"
    >
      <slot name="nuggets">
        <div
          v-for="(nugget, index) in headerNuggets"
          :key="index"
          class="flex flex-row md:gap-2 bg-transparent md:bg-container-bg rounded-2xl md:p-4 h-9 md:h-20 grow"
          :class="[
            nuggetWidth,
            nugget.is_object_id ? 'justify-start gap-4 md:justify-between' : 'justify-between',
            `basis-1/${Math.floor(headerNuggets.length / 2)} md:basis-1/${Math.floor(headerNuggets.length / 2) + 1} lg:basis-auto`,
          ]"
        >
          <div class="flex flex-col min-w-0 items-start" :class="nugget.is_object_id ? '' : ''">
            <span class="text-sm font-semibold text-strong font-inter opacity-40">
              {{ nugget.label }}
            </span>
            <div
              class="text-base md:text-2xl font-semibold md:font-medium text-strongest font-league"
            >
              <slot :name="nugget.field_name">
                <span class="block text-nowrap" :title="nugget.v_model">
                  {{ formatNuggetValue(nugget) }}
                </span>
              </slot>
            </div>
          </div>
          <div v-if="nugget.is_object_id" class="h-full flex items-center justify-center">
            <div class="flex items-center justify-center w-6 h-6">
              <CopyButton :text="nugget.v_model" size="md" />
            </div>
          </div>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { Icon, CopyButton } from "@/components";

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  overtitle: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    default: "",
  },
  headerNuggets: {
    type: Array,
    default: () => [],
  },
  nuggetSize: {
    type: String,
    default: "md",
  },
});

const nuggetWidth = computed(() => {
  switch (props.nuggetSize) {
    case "sm":
      return "md:min-w-24";
    case "md":
      return "md:min-w-36";
    case "lg":
      return "md:min-w-56";
    default:
      return "w-72";
  }
});

function formatNuggetValue(nugget) {
  if (!nugget.v_model) {
    return "-";
  }

  if (nugget.field_type === "number") {
    if (nugget.number_type === "currency") {
      const hasCents = Math.abs(nugget.v_model % 1) > 0;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: hasCents ? 2 : 0,
        maximumFractionDigits: hasCents ? 2 : 0,
      }).format(nugget.v_model);
    } else if (nugget.number_type === "percentage") {
      if (nugget.v_model >= 1) {
        return nugget.v_model + "%";
      } else {
        return nugget.v_model * 100 + "%";
      }
    }
    return nugget.v_model.toLocaleString();
  } else if (nugget.field_type === "date") {
    return new Date(nugget.v_model).toLocaleDateString("en-US", { timeZone: "UTC" });
  }

  if (nugget.is_object_id && typeof nugget.v_model === "string") {
    return `***${nugget.v_model.slice(-4).toUpperCase()}`;
  }

  return nugget.v_model;
}
</script>
